import { ArrowUp, Check, Sparkles, Square } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError, SURVEY_UPDATED_EVENT } from '../../api/client';
import { useI18n, type MessageKey } from '../../i18n';
import { useToast } from '../ui';
import { useAssistant, type AssistantItem, type ProposalItem } from './AssistantContext';
import { Chart } from './Chart';
import { Markdown } from './Markdown';

/**
 * The active conversation with its input box. Used by the floating panel
 * (compact) and the Assistant page (wide, centered column).
 */
export function Conversation({ surveyId, variant }: { surveyId?: string; variant: 'panel' | 'page' }) {
  const { t } = useI18n();
  const { active, status, ask, stop, updateProposal, mock } = useAssistant();
  const [input, setInput] = useState('');
  const scroller = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const busy = status !== null;
  const items = active?.items ?? [];

  useEffect(() => {
    scroller.current?.scrollTo({ top: scroller.current.scrollHeight });
  }, [items.length, status, active?.id]);
  useEffect(() => {
    inputRef.current?.focus();
  }, [active?.id]);

  function send(text: string) {
    if (!text.trim() || busy) return;
    ask(text, surveyId);
    setInput('');
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    send(input);
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault();
      send(input);
    }
  }

  const suggestions: MessageKey[] = surveyId
    ? ['assistant.suggest_results', 'assistant.suggest_trend', 'assistant.suggest_quality', 'assistant.suggest_text', 'assistant.suggest_improve']
    : ['assistant.suggest_list', 'assistant.suggest_new', 'assistant.suggest_compare'];

  return (
    <div className={`assistant-conversation ${variant}`}>
      <div className="assistant-body" ref={scroller} aria-live="polite">
        <div className="assistant-thread">
          {mock && <div className="assistant-demo">{t('assistant.demo_banner')}</div>}
          {items.length === 0 && (
            <div className="assistant-intro">
              {variant === 'page' && (
                <div className="assistant-intro-mark" aria-hidden>
                  <Sparkles />
                </div>
              )}
              {variant === 'page' && <h2>{t('assistant.page_heading')}</h2>}
              <p>{t('assistant.intro')}</p>
              <div className="assistant-suggestions">
                {suggestions.map((key) => (
                  <button key={key} type="button" className="chip" onClick={() => send(t(key))}>
                    {t(key)}
                  </button>
                ))}
              </div>
            </div>
          )}
          {items.map((it, i) =>
            it.kind === 'proposal' ? (
              <ProposalCard key={i} item={it} onChange={(patch) => active && updateProposal(active.id, i, patch)} />
            ) : it.kind === 'chart' ? (
              <div key={i} className="assistant-chart">
                <Chart spec={it.chart} />
              </div>
            ) : it.kind === 'assistant' ? (
              <div key={i} className="assistant-answer">
                <div className="assistant-msg assistant">
                  <Markdown text={it.text} />
                </div>
                <Sources item={it} />
                {!busy && i === items.length - 1 && it.suggestions && it.suggestions.length > 0 && (
                  <div className="assistant-suggestions followups">
                    {it.suggestions.map((q) => (
                      <button key={q} type="button" className="chip" onClick={() => send(q)}>
                        {q}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div key={i} className={`assistant-msg ${it.kind}`}>
                {it.text}
              </div>
            ),
          )}
          {busy && (
            <div className="assistant-status">
              <span className="assistant-dots" aria-hidden>
                <span />
                <span />
                <span />
              </span>
              {status}
            </div>
          )}
        </div>
      </div>

      <form className="assistant-input" onSubmit={submit}>
        <textarea
          ref={inputRef}
          rows={variant === 'page' ? 3 : 2}
          value={input}
          maxLength={4000}
          placeholder={t('assistant.placeholder')}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label={t('assistant.placeholder')}
        />
        {busy ? (
          <button type="button" className="btn icon" onClick={stop} aria-label={t('assistant.stop')} title={t('assistant.stop')}>
            <Square />
          </button>
        ) : (
          <button type="submit" className="btn primary icon" disabled={!input.trim()} aria-label={t('assistant.send')} title={t('assistant.send')}>
            <ArrowUp />
          </button>
        )}
      </form>
      <p className="assistant-note">{t('assistant.disclaimer')}</p>
    </div>
  );
}

/** What the answer's numbers were computed from: questionnaire, responses and filters. */
function Sources({ item }: { item: AssistantItem }) {
  const { t } = useI18n();
  if (!item.sources?.length) return null;
  return (
    <details className="assistant-sources">
      <summary>{t('assistant.sources', { count: item.sources.length })}</summary>
      <ul>
        {item.sources.map((s, i) => (
          <li key={i}>
            <strong>{s.detail || s.survey}</strong>
            <span>
              {s.survey} · {t('assistant.source_responses', { count: s.responses })}
              {s.filters ? ` · ${s.filters}` : ''}
            </span>
          </li>
        ))}
      </ul>
    </details>
  );
}

function ProposalCard({ item, onChange }: { item: ProposalItem; onChange: (patch: Partial<ProposalItem>) => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const nav = useNavigate();
  const p = item.proposal;
  const questions = p.definition.questions;

  async function apply() {
    onChange({ status: 'applying', error: undefined });
    try {
      if (p.kind === 'create') {
        const sv = await api.createSurvey({ title: p.title, description: p.description, definition: p.definition });
        onChange({ status: 'applied' });
        toast(t('assistant.created'));
        nav(`/surveys/${sv.id}`);
        return;
      }
      await api.saveSurvey(p.surveyId!, {
        title: p.title,
        description: p.description,
        requireAuth: !!p.requireAuth,
        definition: p.definition,
        revision: p.revision ?? 0,
      });
      onChange({ status: 'applied' });
      toast(t('assistant.updated'));
      window.dispatchEvent(new CustomEvent(SURVEY_UPDATED_EVENT, { detail: p.surveyId }));
    } catch (e) {
      const conflict = e instanceof ApiError && e.code === 'revision_conflict';
      onChange({ status: 'open', error: conflict ? t('assistant.apply_conflict') : e instanceof ApiError ? e.message : t('common.action_failed') });
    }
  }

  return (
    <div className={`assistant-proposal ${item.status}`}>
      <div className="assistant-proposal-title">
        {p.kind === 'create' ? t('assistant.proposal_create', { title: p.title }) : t('assistant.proposal_edit', { title: p.title })}
      </div>
      {p.summary && <div className="assistant-proposal-summary">{p.summary}</div>}
      <div className="assistant-proposal-meta">
        {p.kind === 'create'
          ? t('assistant.proposal_questions', { count: questions.length })
          : t('assistant.proposal_counts', { added: p.added, updated: p.updated ?? 0, removed: p.removed ?? 0 })}
      </div>
      <details>
        <summary>{t('assistant.preview')}</summary>
        <ol>
          {questions.map((q) => (
            <li key={q.id}>{q.title || t('common.untitled_question')}</li>
          ))}
        </ol>
      </details>
      {item.error && <div className="assistant-proposal-error">{item.error}</div>}
      {item.status === 'applied' ? (
        <div className="assistant-proposal-done">
          <Check /> {t('assistant.applied')}
        </div>
      ) : item.status === 'dismissed' ? (
        <div className="assistant-proposal-done">{t('assistant.dismissed')}</div>
      ) : (
        <div className="row gap-8">
          <button type="button" className="btn primary sm" disabled={item.status === 'applying'} onClick={() => void apply()}>
            {t('assistant.apply')}
          </button>
          <button type="button" className="btn sm" disabled={item.status === 'applying'} onClick={() => onChange({ status: 'dismissed' })}>
            {t('assistant.dismiss')}
          </button>
        </div>
      )}
    </div>
  );
}
